// The language list, as Rails published it on the page.
//
// Rails is the single source of truth (config/supported_locales.yml, injected as
// window.hiikerLocales by layouts/_user_json_window). Keeping a second copy in JS
// is what let the two drift: the client offered en/de long after the manifest
// could have said otherwise, and adding a language meant remembering three files.
//
// Pure and dependency-free so it can be unit-tested without a DOM.

export interface SupportedLocale {
  code: string;
  name: string;
}

// Used when the page didn't publish a list — an old cached layout, a view that
// doesn't render the partial, or a unit test. English alone is the honest
// fallback: it's the one locale we know is always present.
export const FALLBACK_LOCALES: SupportedLocale[] = [{ code: "en", name: "English" }];

// Accepts whatever was on `window` and returns a list we can rely on. Anything
// malformed degrades to English rather than throwing — a broken language menu
// must not take the page's JS down with it.
export const parseLocales = (raw: unknown): SupportedLocale[] => {
  if (!Array.isArray(raw)) return FALLBACK_LOCALES;

  const parsed = raw.filter(
    (entry): entry is SupportedLocale =>
      !!entry &&
      typeof entry === "object" &&
      typeof (entry as SupportedLocale).code === "string" &&
      (entry as SupportedLocale).code.length > 0 &&
      typeof (entry as SupportedLocale).name === "string",
  );

  return parsed.length > 0 ? parsed : FALLBACK_LOCALES;
};

// Which locale to start in. Rails already resolved this — user preference,
// session, then Accept-Language — and stamped it on the page, so trust it over
// re-deriving from navigator.language and risking a flash of the wrong language.
// The <html lang> attribute is the fallback for pages that predate the change.
export const resolveInitialLocale = (
  published: unknown,
  htmlLang: string | null | undefined,
  available: SupportedLocale[],
): string => {
  const codes = available.map((l) => l.code);
  const candidates = [published, htmlLang];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && codes.includes(candidate)) return candidate;
  }

  // "de-AT" should land on "de" rather than falling all the way to English.
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const base = candidate.split("-")[0];
    if (codes.includes(base)) return base;
  }

  return codes.includes("en") ? "en" : codes[0];
};

export const supportedLocales = (): SupportedLocale[] =>
  parseLocales(typeof window === "undefined" ? undefined : (window as never as Record<string, unknown>).hiikerLocales);
